import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getLivenessStats, getSekolahList } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Progress } from '../ui/progress';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  ShieldCheck, ShieldAlert, ShieldOff, Users, TrendingUp, 
  Calendar, RefreshCw, Loader2, AlertTriangle, CheckCircle2,
  XCircle, BarChart3
} from 'lucide-react';
import { toast } from '../ui/sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const MONTHS = [
  { value: 1, label: 'Januari' },
  { value: 2, label: 'Februari' },
  { value: 3, label: 'Maret' },
  { value: 4, label: 'April' },
  { value: 5, label: 'Mei' },
  { value: 6, label: 'Juni' },
  { value: 7, label: 'Juli' },
  { value: 8, label: 'Agustus' },
  { value: 9, label: 'September' },
  { value: 10, label: 'Oktober' },
  { value: 11, label: 'November' },
  { value: 12, label: 'Desember' }
];

const COLORS = {
  verified: '#10b981',    // emerald-500
  skipped: '#f59e0b',     // amber-500
  noData: '#9ca3af',      // gray-400
  face: '#6366f1'         // indigo-500
};

const LivenessReportPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [schools, setSchools] = useState([]);
  
  // Filters
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedSchool, setSelectedSchool] = useState('all');

  const isAdmin = user?.role === 'admin_dinas' || user?.role === 'pimpinan';

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const sekolahId = selectedSchool !== 'all' ? selectedSchool : null;
      const data = await getLivenessStats(selectedMonth, selectedYear, sekolahId);
      setStats(data);
    } catch (error) {
      console.error('Error fetching liveness stats:', error);
      toast.error('Gagal memuat statistik liveness');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, selectedSchool]);

  const fetchSchools = useCallback(async () => {
    if (isAdmin) {
      try {
        const data = await getSekolahList();
        setSchools(data);
      } catch (error) {
        console.error('Error fetching schools:', error);
      }
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchStats();
    fetchSchools();
  }, [fetchStats, fetchSchools]);

  // Prepare pie chart data
  const pieData = stats ? [
    { name: 'Liveness Verified', value: stats.summary.liveness_verified, color: COLORS.verified },
    { name: 'Liveness Skipped', value: stats.summary.liveness_skipped, color: COLORS.skipped },
    { name: 'Data Lama', value: stats.summary.no_liveness_data, color: COLORS.noData }
  ].filter(d => d.value > 0) : [];

  // Generate years for dropdown
  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 2; y--) {
    years.push(y);
  }

  return (
    <div className="space-y-6" data-testid="liveness-report-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-emerald-600" />
            Laporan Verifikasi Liveness
          </h1>
          <p className="text-gray-500 mt-1">
            Statistik verifikasi anti-spoofing pada absensi
          </p>
        </div>
        
        <Button onClick={fetchStats} disabled={loading} variant="outline">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[150px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Bulan</label>
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 min-w-[120px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Tahun</label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {isAdmin && schools.length > 0 && (
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Sekolah</label>
                <Select value={selectedSchool} onValueChange={setSelectedSchool}>
                  <SelectTrigger>
                    <SelectValue placeholder="Semua Sekolah" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Sekolah</SelectItem>
                    {schools.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.nama}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : stats ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500 rounded-lg">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-700">Total Absensi</p>
                    <p className="text-2xl font-bold text-blue-900">{stats.summary.total_absensi}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-500 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-700">Liveness Verified</p>
                    <p className="text-2xl font-bold text-emerald-900">{stats.summary.liveness_verified}</p>
                    <p className="text-xs text-emerald-600">{stats.percentages.liveness_rate}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-500 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-700">Liveness Skipped</p>
                    <p className="text-2xl font-bold text-amber-900">{stats.summary.liveness_skipped}</p>
                    <p className="text-xs text-amber-600">{stats.percentages.skip_rate}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-500 rounded-lg">
                    <ShieldCheck className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-indigo-700">Face Verified</p>
                    <p className="text-2xl font-bold text-indigo-900">{stats.summary.face_verified}</p>
                    <p className="text-xs text-indigo-600">{stats.percentages.face_rate}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Distribusi Verifikasi Liveness
                </CardTitle>
                <CardDescription>
                  Perbandingan status verifikasi liveness bulan ini
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[250px] text-gray-500">
                    Tidak ada data
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Daily Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Tren Harian
                </CardTitle>
                <CardDescription>
                  Verifikasi liveness per hari
                </CardDescription>
              </CardHeader>
              <CardContent>
                {stats.daily_breakdown.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={stats.daily_breakdown}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 10 }}
                        tickFormatter={(value) => value.split('-')[2]}
                      />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip 
                        labelFormatter={(value) => `Tanggal: ${value}`}
                      />
                      <Legend />
                      <Bar dataKey="verified" name="Verified" fill={COLORS.verified} stackId="a" />
                      <Bar dataKey="skipped" name="Skipped" fill={COLORS.skipped} stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[250px] text-gray-500">
                    Tidak ada data
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Top Skippers Table */}
          {stats.top_skippers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <ShieldOff className="w-5 h-5" />
                  Guru yang Sering Skip Verifikasi Liveness
                </CardTitle>
                <CardDescription>
                  Top 10 guru yang paling sering melewati verifikasi liveness bulan ini
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4">#</th>
                        <th className="text-left py-3 px-4">Nama Guru</th>
                        <th className="text-left py-3 px-4">Sekolah</th>
                        <th className="text-center py-3 px-4">Jumlah Skip</th>
                        <th className="text-left py-3 px-4">Progress</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top_skippers.map((skipper, idx) => (
                        <tr key={skipper.guru_id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium text-gray-500">{idx + 1}</td>
                          <td className="py-3 px-4 font-medium">{skipper.guru_nama}</td>
                          <td className="py-3 px-4 text-gray-600">{skipper.sekolah_nama}</td>
                          <td className="py-3 px-4 text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              {skipper.skip_count}x
                            </span>
                          </td>
                          <td className="py-3 px-4 w-32">
                            <Progress 
                              value={Math.min((skipper.skip_count / (stats.top_skippers[0]?.skip_count || 1)) * 100, 100)} 
                              className="h-2"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info Alert */}
          <Alert className="border-blue-200 bg-blue-50">
            <ShieldCheck className="w-4 h-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Tentang Verifikasi Liveness:</strong> Fitur ini memastikan bahwa absensi dilakukan oleh orang asli, 
              bukan menggunakan foto atau video. Guru diminta untuk menolehkan kepala ke kiri dan kanan saat absensi.
              Jika verifikasi dilewati, absensi tetap tercatat tetapi akan muncul sebagai security alert.
            </AlertDescription>
          </Alert>
        </>
      ) : (
        <Alert className="border-red-200 bg-red-50">
          <XCircle className="w-4 h-4 text-red-600" />
          <AlertDescription className="text-red-800">
            Gagal memuat data. Silakan coba lagi.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default LivenessReportPage;
